/**
 * schemas/reconciliation
 * ──────────────────────────────────────────────────────────────────────────
 * A cash count and what its difference costs someone.
 *
 * Two kinds, and they measure different things:
 *   limit       expected (the assignment's limit) vs counted
 *   collection  owed (from Billing) vs collected vs deposited
 *
 * The stored kind is `imprest` / `collection`; it DISPLAYS as "limit" /
 * "collection" (lib/status.ts `reconKindLabel`). The count POST takes the
 * display word, which is the API's own choice — see countSchema.
 *
 * No variance is ever absorbed (invariant 3): a difference is either routed to
 * Payroll/Billing or waived with a reason. There is no third option, and the
 * card's footer says which one happened.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { z } from 'zod';
import { currency, isoDate, isoDateNullable, minor, minorNullable } from '../common';
import { gateStateSchema } from '../config/gates';

export const RECON_STATUSES = ['matched', 'short', 'over', 'waived'] as const;
export type ReconStatus = (typeof RECON_STATUSES)[number];

export const reconciliationSchema = z.object({
  id: z.string(),
  assignment_id: z.string(),
  holder: z.string(),
  /** Free-text period label, e.g. `2026-08`. */
  period: z.string(),
  /** Stored `imprest` | `collection`. Displayed "limit" | "collection". */
  kind: z.string(),
  cur: currency,
  /** The limit, or the owed figure — never taken from the request. */
  expected_minor: minor,
  counted_minor: minorNullable,
  /** counted − expected. Negative is short, positive is over. */
  variance_minor: minorNullable,
  // Collection legs. Null on a limit count.
  collected_minor: minorNullable,
  owed_minor: minorNullable,
  deposited_minor: minorNullable,
  status: z.string(),
  /** `payroll` | `billing` once routed. */
  route_target: z.string().nullable(),
  /** The reference the destination returned — `bil_jr_01205`. */
  route_ref: z.string().nullable(),
  /** Required on a waive. */
  reason: z.string().nullable(),
  reconciled_by: z.string().nullable(),
  reconciled_at: isoDateNullable,
  created_at: isoDate,
  /**
   * The API's own plain-language footer: "This INR 80.00 surplus will be
   * posted to Billing." Rendered verbatim — the consequence of a count is the
   * backend's to state, not this app's to guess.
   */
  consequence: z.string(),
});
export type Reconciliation = z.infer<typeof reconciliationSchema>;

/** GET /v1/reconciliation — the gate rides in the body with a 200. */
export const reconciliationsResponseSchema = z.object({
  gates: gateStateSchema,
  reconciliations: z.array(reconciliationSchema),
});
export type ReconciliationResponse = z.infer<typeof reconciliationsResponseSchema>;

/** POST /v1/reconciliation/counts — the Cash count modal. */
export const countCreateSchema = z.object({
  assignment_id: z.string(),
  period: z.string().min(1).max(20),
  /** The API takes the DISPLAY word here, not the stored `imprest`. */
  kind: z.enum(['limit', 'collection']),
  counted_minor: z.string().optional(),
  collected_minor: z.string().optional(),
  owed_minor: z.string().optional(),
  deposited_minor: z.string().optional(),
  owed_ref: z.string().max(200).optional(),
});
export type CountCreate = z.infer<typeof countCreateSchema>;

/** POST /v1/reconciliation/:id/resolve — waiving requires a reason. */
export const resolveVarianceSchema = z
  .object({
    action: z.enum(['route', 'waive']),
    reason: z.string().min(1).max(1000).optional(),
  })
  .refine((v) => v.action !== 'waive' || !!v.reason?.trim(), {
    message: 'Waiving a variance requires a reason.',
    path: ['reason'],
  });
export type ResolveVariance = z.infer<typeof resolveVarianceSchema>;

/** Which legs a card renders. Collection counts show four, limit counts three. */
export const isCollectionCount = (r: Reconciliation): boolean => r.kind === 'collection';

/** True when the card still needs a decision — Waive / Route are shown. */
export const needsRouting = (r: Reconciliation): boolean =>
  (r.status === 'short' || r.status === 'over') && !r.reconciled_at;
